/**
 * Effective-dated prices, terms and pay (decisions-log V6–V9). The entry in force for a month is
 * the one with the greatest `effectiveFrom` on or before the month's last day.
 */
import { periodEnd } from '../dates';

/**
 * How an assignment is paid (V7, V69, V184): by the hour (worked or billed to the client), a monthly fee, a scope total, a share of the
 * project's revenue or profit, or the salary through one of our entities.
 */
export type PayFormat = 'hourly' | 'billed_hourly' | 'monthly' | 'scope' | 'revenue_share' | 'profit_share' | 'salary';
export const SHARE_FORMATS: readonly PayFormat[] = ['revenue_share', 'profit_share'];
/** Formats paid by a rate per hour (`rateE4`): the person's hours, or the hours billed to the client (V184). */
export const isRateFormat = (format: string | null | undefined): boolean => format === 'hourly' || format === 'billed_hourly';
/** Formats whose month amount comes from outside the line (the project's figures, the salary). */
export const isComputedFormat = (format: PayFormat): boolean => SHARE_FORMATS.includes(format) || format === 'salary';
export type BillingMode = 'hourly' | 'fixed' | 'scope' | 'payroll' | 'internal';

export interface Dated {
  readonly effectiveFrom: string;
}

/** A price-like entry: `rateE4` for hourly, `amountMinor` for monthly, fixed, payroll and scope. */
export interface PriceEntry extends Dated {
  readonly rateE4: number | null;
  readonly amountMinor: number | null;
  readonly currency: string;
}

export interface PayEntry extends PriceEntry {
  readonly format: PayFormat;
  /** revenue_share / profit_share: the share × 1 000 000 (V69). */
  readonly shareE6?: number | null;
  /** salary: the entity the salary is paid through (V69). */
  readonly issuerId?: string | null;
}

/** Latest entry with `effectiveFrom` ≤ `date`. */
export function entryOn<T extends Dated>(entries: readonly T[], date: string): T | null {
  let best: T | null = null;
  for (const entry of entries) {
    if (entry.effectiveFrom <= date && (!best || entry.effectiveFrom >= best.effectiveFrom)) best = entry;
  }
  return best;
}

/** Entry in force for a month: on or before its last day (V8). */
export function entryForMonth<T extends Dated>(entries: readonly T[], period: string): T | null {
  return entryOn(entries, periodEnd(period));
}

/**
 * The client price of a project for a month, as the billing mode reads it. The first price also holds
 * for the months before its date (V248): the card shows it as the project's rate from its start.
 */
export function projectPriceFor(
  mode: BillingMode,
  prices: readonly PriceEntry[],
  period: string,
): PriceEntry | null {
  if (mode === 'internal') return null;
  const relevant = prices.filter((price) => (mode === 'hourly' ? price.rateE4 != null : price.amountMinor != null));
  const earliest = [...relevant].sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom))[0] ?? null;
  return entryForMonth(relevant, period) ?? earliest;
}

/** Only the assignment's own rate pays (V201, V245). */
export type PaySource = 'assignment';

export interface ResolvedPay extends PayEntry {
  readonly source: PaySource;
}

/** Assignment pay for a month: its own rows («своя ставка»); none — no rate. */
export function payFor(assignmentPay: readonly PayEntry[], period: string): ResolvedPay | null {
  const own = entryForMonth(assignmentPay, period);
  return own ? { ...own, source: 'assignment' } : null;
}
